/**
 * @file src/pages/company-onboarding-wizard/components/WizardShell.jsx
 * @description Wizard layout shell — header, tab navigation, progress bar.
 *
 * Responsibilities:
 *  - Render the card wrapper, gradient header, tab switcher
 *  - Render the step progress indicator
 *  - Render children (step content)
 *
 * Rules:
 *  - No state, no API calls, no business logic
 *  - Pure presentational component — all data via props
 *  - STEPS and TABS imported from wizard constants (not re-defined here)
 */

import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Shield, Building, UserPlus, CheckCircle, X } from "lucide-react";
import { STEPS, TABS } from "../constants";
import { ROUTES } from "config/routes";

// ---------------------------------------------------------------------------
// Step progress indicator
// ---------------------------------------------------------------------------

const StepProgress = ({ currentStep }) => (
  <div className="mb-8">
    {/* Step circles + connectors */}
    <div className="flex justify-between items-center mb-4">
      {STEPS.map((step, index) => (
        <div key={step.number} className="flex items-center flex-1">
          {/* Circle */}
          <div
            className={`
              flex items-center justify-center w-10 h-10 rounded-full
              shrink-0 transition-colors duration-200
              ${
                currentStep >= step.number
                  ? "bg-primary text-white"
                  : "bg-gray-200 text-gray-400"
              }
            `}
            aria-label={`Step ${step.number}: ${step.title}`}
          >
            {currentStep > step.number ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <step.icon className="w-5 h-5" />
            )}
          </div>

          {/* Connector line */}
          {index < STEPS.length - 1 && (
            <div
              className={`
                flex-1 h-0.5 mx-4 transition-colors duration-200
                ${currentStep > step.number ? "bg-primary" : "bg-gray-200"}
              `}
            />
          )}
        </div>
      ))}
    </div>

    {/* Current step label */}
    <div className="text-center">
      <h2 className="text-xl font-semibold text-gray-900">
        {STEPS[currentStep - 1]?.title}
      </h2>
      <p className="text-gray-600 mt-1 text-sm">
        {STEPS[currentStep - 1]?.description}
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Tab navigation
// ---------------------------------------------------------------------------

const TAB_CONFIG = [
  { key: TABS.CREATE, label: "Create Company", Icon: Building },
  { key: TABS.JOIN, label: "Join Company", Icon: UserPlus },
];

const TabNav = ({ activeTab, onTabChange }) => (
  <div className="border-b border-gray-200">
    <nav className="flex" role="tablist">
      {TAB_CONFIG.map(({ key, label, Icon }) => (
        <button
          key={key}
          role="tab"
          aria-selected={activeTab === key}
          onClick={() => onTabChange(key)}
          className={`
            flex-1 py-4 px-6 text-sm font-medium transition-colors
            ${
              activeTab === key
                ? "border-b-2 border-primary text-primary bg-primary-50"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }
          `}
        >
          <Icon className="w-4 h-4 inline mr-2" />
          {label}
        </button>
      ))}
    </nav>
  </div>
);

// ---------------------------------------------------------------------------
// WizardShell
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   activeTab:    string,
 *   currentStep:  number,
 *   onTabChange:  (tab: string) => void,
 *   children:     React.ReactNode,
 *   submitError?: string,
 * }} props
 */
const WizardShell = ({
  activeTab,
  currentStep,
  onTabChange,
  children,
  submitError,
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* ── Main card ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold">Company Onboarding</h1>
                  <p className="text-primary-100 text-sm">
                    ZATCA-compliant e-invoicing setup
                  </p>
                </div>
              </div>

              <button
                onClick={() => navigate(ROUTES.DASHBOARD)}
                aria-label="Close onboarding and go to dashboard"
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tab navigation */}
          <TabNav activeTab={activeTab} onTabChange={onTabChange} />

          {/* Content area */}
          <div className="p-6">
            {/* Global submit error banner */}
            {submitError && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg text-sm text-error-700"
                role="alert"
              >
                {submitError}
              </motion.div>
            )}

            {/* Step progress — only shown on create tab */}
            {activeTab === TABS.CREATE && (
              <StepProgress currentStep={currentStep} />
            )}

            {/* Step / join content injected here */}
            {children}
          </div>
        </motion.div>

        {/* ── ZATCA trust badge ────────────────────────────────────────────── */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm">
            <Shield className="w-4 h-4 text-primary" />
            <span>ZATCA Phase 2 Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardShell;
